import { Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import { EMERGENCY_CONTACT, SEX_LIST } from '@/common/constants/common';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { useGetDicListQuery } from '@/api/common';
import AppAutoCompleteMenu from '@/common/components/form-inputs/AppAutoCompleteMenu';
import { eightDigitPhone } from '@/utils/validationHelper';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditEmergencyContactForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();
		const defaultValues: DefaultValues<any> = {
			hasEmergencyContact: null,
		};

		const { data: relationshipDicList } = useGetDicListQuery('relationship', {
			refetchOnMountOrArgChange: true,
		});

		useEffect(() => {
			if (details) methods.reset(details);
		}, [details]);

		const formSchema = Yup.object().shape({
			hasEmergencyContact: Yup.string().required(
				t('errorMessage.pleaseSelect')
			),
			emergencyContactName: Yup.string().when('hasEmergencyContact', {
				is: 'true',
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),

			emergencyContactAge: Yup.string().when('hasEmergencyContact', {
				is: 'true',
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),

			emergencyContactRelationship: Yup.string().when('hasEmergencyContact', {
				is: 'true',
				then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
				otherwise: (schema) => schema.nullable(),
			}),

			emergencyContactAddress: Yup.string().when('hasEmergencyContact', {
				is: 'true',
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),

			emergencyContactTelNo: Yup.string()
				.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits')))
				.when('hasEmergencyContact', {
					is: 'true',
					then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
					otherwise: (schema) => schema.nullable(),
				}),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		const watchHasEmergencyContact = methods.watch('hasEmergencyContact');

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.emergencyContactMan')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e: any) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.isEmergencyContact') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='hasEmergencyContact'
									row
									options={EMERGENCY_CONTACT}
									handleChange={(e: any) => {
										if (e.target.value === 'false') {
											methods.setValue('emergencyContactName', '');
											methods.setValue('emergencyContactAge', '');
											methods.setValue('emergencyContactRelationship', '');
											methods.setValue('emergencyContactAddress', '');
											methods.setValue('emergencyContactTelNo', '');
										}
									}}
								/>
							</Grid>
							{(watchHasEmergencyContact === 'true' ||
								watchHasEmergencyContact === true) && (
								<>
									<Grid size={6}>
										<AppTextField
											control={methods.control}
											name='emergencyContactName'
											label={t('preAdmission.emergencyContactName') + '*'}
										/>
									</Grid>
									<Grid size={6}>
										<AppTextField
											control={methods.control}
											name='emergencyContactAge'
											label={t('preAdmission.emergencyContactAge') + '*'}
										/>
									</Grid>
									<Grid size={6}>
										<AppAutoCompleteMenu
											control={methods.control}
											name='emergencyContactRelationship'
											label={
												t('preAdmission.emergencyContactRelationship') + '*'
											}
											options={(relationshipDicList?.data || []).map(
												(item: { labelZh: any }) => {
													return {
														...item,
														name: item.labelZh,
													};
												}
											)}
										/>
									</Grid>
									<Grid size={6}>
										<AppTextField
											control={methods.control}
											name='emergencyContactAddress'
											label={t('preAdmission.emergencyContactAddress') + '*'}
										/>
									</Grid>
									<Grid size={6}>
										<AppTextField
											control={methods.control}
											name='emergencyContactTelNo'
											label={t('preAdmission.emergencyContactTelNo') + '*'}
										/>
									</Grid>
								</>
							)}
						</Grid>

						<Divider sx={{ marginBottom: '16px' }} />
					</form>
				</FormProvider>
			</>
		);
	}
);

EditEmergencyContactForm.displayName = 'EditEmergencyContactForm';

export default EditEmergencyContactForm;
